import React, { useEffect, useRef } from "react";
import { Select } from "antd";
import useLocation from "../../hooks/useLocation";
import { isAllLocationSelected } from "../../library/helpers";

const OptionLabel = ({ prefix, label }) => (
  <span>
    {prefix && <span className="font-medium text-secondary">{prefix}</span>}
    {prefix ? " " : ""}
    {label}
  </span>
);

const LocationSelect = ({
  variables = {},
  skip = false,
  value,
  onChange,
  placeholder = "Select Location",
  labelKey = "name",
  valueKey = "id",
  allowClear = true,
  className = "",
  maxTagCount = "responsive",
  showAllLocationOption = false, // new prop
  autoSelectFirst = false, // New prop: auto-select first location when data loads
  fetchPolicy,
  successCallback,
  addLocationTypeLabel = true,
  isLoading = false,
  selectWidth = "",
  ...rest
}) => {
  const { data, loading, lazyData, lazyLoading } = useLocation({
    variables,
    skip,
    fetchPolicy,
    successCallback,
  });

  // Prefer lazyData if fetchLocations was used, else use data
  const locations = (lazyData && lazyData.getLocations) || (data && data.getLocations) || [];

  // Track whether auto-select has already fired, so clearing doesn't re-trigger it
  const hasAutoSelected = useRef(false);

  // Auto-select first location when data loads (if enabled and no value is set)
  useEffect(() => {
    if (
      autoSelectFirst &&
      !value &&
      locations.length > 0 &&
      !loading &&
      !lazyLoading &&
      !hasAutoSelected.current
    ) {
      hasAutoSelected.current = true;
      const firstLocation = locations[0];
      const firstValue = String(firstLocation[valueKey]);
      onChange?.(rest.mode === "multiple" ? [firstValue] : firstValue);
    }
  }, [autoSelectFirst, value, locations, loading, lazyLoading, valueKey, onChange, rest.mode]);

  // Determine display value
  const getDisplayValue = () => {
    if (!showAllLocationOption) {
      return value;
    }

    // If value is null or empty and showAllLocationOption is true, default to "All Locations"
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return rest.mode === "multiple" ? ["all-locations"] : "all-locations";
    }

    return value;
  };

  // Handle value changes with "All Locations" logic
  const handleChange = selectedValues => {
    if (!showAllLocationOption) {
      onChange?.(selectedValues);
      return;
    }

    // If multiple mode and array is provided
    if (Array.isArray(selectedValues)) {
      const currentDisplayValue = getDisplayValue();
      const wasAllLocationsSelected =
        Array.isArray(currentDisplayValue) && currentDisplayValue.includes("all-locations");

      // If "All Locations" is in the new selection
      if (selectedValues.includes("all-locations")) {
        // If "All Locations" was NOT previously selected, user just clicked it
        if (!wasAllLocationsSelected) {
          onChange?.(null); // Send null when "All Locations" is selected
        } else {
          // If "All Locations" was already selected and user selected other options,
          // remove "All Locations" and keep only the specific locations
          const filteredValues = selectedValues.filter(val => val !== "all-locations");
          onChange?.(filteredValues.length > 0 ? filteredValues : null);
        }
      } else {
        // "All Locations" is not in selection - normal specific location selection
        onChange?.(selectedValues.length > 0 ? selectedValues : null);
      }
    } else if (selectedValues === "all-locations") {
      // Single selection mode - "All Locations" selected
      onChange?.(null);
    } else {
      // Single selection mode - specific location selected
      onChange?.(selectedValues);
    }
  };

  // Custom filter function for search
  const filterOption = (input, option) => {
    try {
      // Handle null or undefined input
      if (!input) return true;

      // Handle "All Locations" option
      if (option?.value === "all-locations") {
        return "all locations".toLowerCase().includes(input.toLowerCase());
      }

      // Handle missing option or value
      if (!option || !option.value) return false;

      // Find the location data for this option
      const location = locations.find(loc => String(loc?.[valueKey]) === String(option.value));
      if (!location) return false;

      const searchText = String(input).toLowerCase();
      const displayLabel = location[labelKey] || location.title || location.name || "";
      const title = String(displayLabel).toLowerCase();
      const locationType = String(location.locationType || "").toLowerCase();

      // Search in both title and location type
      return title.includes(searchText) || locationType.includes(searchText);
    } catch (error) {
      console.error("Error in LocationSelect filterOption:", error);
      return false;
    }
  };

  // Build options array including pre-selected values that may not be loaded yet
  const buildOptions = () => {
    const options = [];

    // Add "All Locations" option if enabled
    if (showAllLocationOption) {
      options.push({
        label: <OptionLabel label="All Locations" />,
        tagLabel: "All Locations",
        value: "all-locations",
      });
    }

    // Add loaded locations
    locations.forEach(loc => {
      const displayLabel = loc[labelKey] || loc.title || loc.name;
      const tagLabel = loc.locationType ? `${loc.locationType}: ${displayLabel}` : displayLabel;
      options.push({
        label: (
          <OptionLabel
            prefix={loc.locationType ? `${loc.locationType}:` : null}
            label={displayLabel}
          />
        ),
        // Used for tag display when optionLabelProp="tagLabel"
        tagLabel: tagLabel,
        value: String(loc[valueKey]),
      });
    });

    // If data is still loading and we have a value that's not in loaded locations,
    // add placeholder options for those values
    if ((loading || lazyLoading) && value) {
      const valuesToCheck = Array.isArray(value) ? value : [value];
      valuesToCheck.forEach(val => {
        if (
          val &&
          val !== "all-locations" &&
          !locations.find(loc => String(loc[valueKey]) === String(val))
        ) {
          options.push({
            label: "Loading...",
            tagLabel: "Loading...",
            value: String(val),
          });
        }
      });
    }

    return options;
  };

  return (
    <Select
      showSearch
      className={`${selectWidth || "w-full"} ${className}`}
      allowClear={allowClear}
      loading={loading || lazyLoading || isLoading}
      value={getDisplayValue()}
      onChange={handleChange}
      maxTagCount={maxTagCount}
      placeholder={placeholder}
      filterOption={filterOption}
      notFoundContent={loading || lazyLoading ? "Location is being fetched..." : undefined}
      options={buildOptions()}
      optionLabelProp="tagLabel"
      {...rest}
    />
  );
};

export default LocationSelect;
